import React from 'react';
import { captureException, capture } from '../../utils/analytics';
import './ErrorBoundary.css';

// Shared with the `vite:preloadError` handler in main.jsx. After a deploy,
// hashed chunks are renamed and the old ones drop off the CDN. A stale client
// either (a) fails the dynamic import outright (caught by vite:preloadError) or
// (b) resolves a mismatched chunk graph so a React.lazy() module arrives without
// a `default` export — which surfaces here as a TypeError, NOT as preloadError.
// Both recover the same way: reload ONCE to pull the fresh index.html + current
// chunk hashes. The shared sessionStorage key guards against a reload loop on a
// genuinely-broken deploy (and against the two handlers double-reloading).
const RELOAD_KEY = 'tm.preloadReloadedAt';
const RELOAD_WINDOW_MS = 10_000;

/**
 * True for errors caused by stale/mismatched JS chunks after a deploy — failed
 * dynamic imports and the React.lazy "module has no default export" variant.
 * These are recoverable by reloading, not real application bugs.
 */
function isChunkLoadError(error) {
  if (!error) return false;
  const msg = String((error && error.message) || error || '');
  const name = String((error && error.name) || '');
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /is not a valid JavaScript MIME type/i.test(msg) ||
    /Loading (?:CSS )?chunk \d+ failed/i.test(msg) ||
    // React.lazy: a stale chunk resolves to a module without a `default` export.
    // Chrome wording then iOS Safari wording.
    /Cannot read properties of undefined \(reading 'default'\)/i.test(msg) ||
    /(?:undefined|null) is not an object \(evaluating '[^']*\.default'\)/i.test(msg)
  );
}

/**
 * React Error Boundary component
 * Catches JavaScript errors in child component tree and displays a fallback UI
 * Also reports errors to PostHog for tracking
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
      // Set once a chunk-error reload has already been attempted this window, so
      // a genuinely-broken deploy falls through to the real fallback UI instead
      // of looping reloads or sitting on a blank "updating" screen forever.
      reloadExhausted: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      let canReload = true;
      try {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        canReload = Date.now() - last > RELOAD_WINDOW_MS;
        if (canReload) sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        canReload = true; // private mode / storage disabled — best-effort reload
      }
      if (canReload) {
        // Breadcrumb, not an exception — recovery is silent and expected, so we
        // don't want it polluting error tracking.
        try { capture('chunk_reload_recovery', { source: 'error_boundary' }); } catch { /* noop */ }
        window.location.reload();
        return;
      }
      // Already reloaded within the window → deploy is genuinely broken. Surface
      // it as a real error and show the fallback UI.
      this.setState({ reloadExhausted: true });
    }

    // Log error to PostHog (skip the auto-recovering chunk case handled above).
    captureException(error, {
      componentStack: errorInfo.componentStack,
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      chunkError: isChunkLoadError(error)
    });

    // Also log to console for development
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/trades';
  };

  render() {
    if (this.state.hasError) {
      // Stale-chunk error: componentDidCatch is reloading the page. Render a
      // neutral placeholder (not the scary error screen) for the frame or two
      // before navigation, unless the reload guard already fired this window.
      if (this.state.isChunkError && !this.state.reloadExhausted) {
        return (
          <div className="error-boundary">
            <div className="error-boundary-content">
              <p className="error-message">Updating to the latest version…</p>
            </div>
          </div>
        );
      }
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">⚠️</div>
            <h1>Something went wrong</h1>
            <p className="error-message">
              We're sorry, but something unexpected happened.
              Our team has been notified.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}

            <div className="error-actions">
              <button
                className="error-btn primary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
              <button
                className="error-btn secondary"
                onClick={this.handleGoHome}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
